import { fetchWithSessionHandling } from './utils/authFetch';
import { ApiResponseError } from './utils/apiErrorMessage';
import { notifyRateLimited } from './utils/rateLimitEvents';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const handleRateLimitResponse = (response: Response): boolean => {
  if (response.status !== 429) {
    return false;
  }

  notifyRateLimited({ source: 'fetch', retryAfter: response.headers.get('Retry-After') });
  return true;
};

// APIからのレスポンスの型
export interface DashboardSummaryResponse {
  filename: string;
  total: number;
  firstDate: string | null;
  lastDate: string | null;
  uniqueDays: number;
  uniqueDepartments: number;
  filterOptions: {
    departments: string[];
    occupations: string[];
    timings: string[];
    reasons: string[];
  };
}

export interface DashboardAggregateRow {
  group: Record<string, string>;
  result?: string;
  reason?: string;
  count: number;
}

export interface DashboardAggregatesResponse {
  groupBy: string[];
  total: number;
  compliant: number;
  complianceRate: number;
  firstDate: string | null;
  lastDate: string | null;
  uniqueDays: number;
  uniqueDepartments: number;
  results: DashboardAggregateRow[];
  reasons: DashboardAggregateRow[];
}

export interface DashboardTimeseriesResponse {
  metric: string;
  granularity: string;
  series: Array<{
    bucket: string;
    result: string;
    reason: string;
    occupation: string;
    count: number;
  }>;
}

export interface DashboardQueryBundle {
  summary: DashboardSummaryResponse;
  aggregates: DashboardAggregatesResponse;
  timeseries: DashboardTimeseriesResponse;
}


const appendDashboardFilters = (
  params: URLSearchParams,
  filters: {
    dateRange: { start?: string; end?: string };
    departments: string[];
    occupations: string[];
    timings: string[];
    reasons: string[];
  },
) => {
  if (filters.dateRange.start) params.set('start', filters.dateRange.start);
  if (filters.dateRange.end) params.set('end', filters.dateRange.end);
  filters.departments.forEach(value => params.append('departments', value));
  filters.occupations.forEach(value => params.append('occupations', value));
  filters.timings.forEach(value => params.append('timings', value));
  filters.reasons.forEach(value => params.append('reasons', value));
};

const fetchDashboardJson = async <T>(path: string): Promise<T> => {
  const { response, sessionExpired } = await fetchWithSessionHandling(
    `${API_BASE_URL}${path}`,
    { credentials: 'include' },
  );
  if (handleRateLimitResponse(response)) {
    throw new ApiResponseError(429, 'rate_limited');
  }
  if (sessionExpired) {
    throw new Error('session_expired');
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => null);
    throw new ApiResponseError(response.status, payload?.detail || 'dashboard_request_failed');
  }
  return response.json();
};

export const fetchDashboardQueryBundle = async (
  fileId: string,
  filters: {
    dateRange: { start?: string; end?: string };
    departments: string[];
    occupations: string[];
    timings: string[];
    reasons: string[];
  },
): Promise<DashboardQueryBundle> => {
  const aggregateParams = new URLSearchParams();
  ['department', 'occupation', 'timing'].forEach(value => {
    aggregateParams.append('groupBy', value);
  });
  appendDashboardFilters(aggregateParams, filters);

  const timeseriesParams = new URLSearchParams({
    metric: 'resultBreakdown',
    granularity: 'month',
  });
  appendDashboardFilters(timeseriesParams, filters);

  const [summary, aggregates, timeseries] = await Promise.all([
    fetchDashboardJson<DashboardSummaryResponse>(
      `/api/files/dashboard/${fileId}/summary`,
    ),
    fetchDashboardJson<DashboardAggregatesResponse>(
      `/api/files/dashboard/${fileId}/aggregates?${aggregateParams.toString()}`,
    ),
    fetchDashboardJson<DashboardTimeseriesResponse>(
      `/api/files/dashboard/${fileId}/timeseries?${timeseriesParams.toString()}`,
    ),
  ]);
  return { summary, aggregates, timeseries };
};



// Notice 型
export interface Notice {
  id: number;
  title: string;
  body: string;
  published_at: string;
  is_read: boolean;
  target_app?: string;
}

// お知らせ一覧を取得
export const getNotices = async (): Promise<Notice[]> => {
  const authAttempt = await fetchWithSessionHandling(`${API_BASE_URL}/api/notice`, {
    credentials: 'include',
  });
  let response = authAttempt.response;
  const sessionExpired = authAttempt.sessionExpired;
  if (response.status === 401) {
    const publicAttempt = await fetchWithSessionHandling(`${API_BASE_URL}/api/notice/public`, {
      credentials: 'include',
    });
    response = publicAttempt.response;
  }

  if (handleRateLimitResponse(response)) {
    throw new ApiResponseError(429, 'rate_limited');
  }

  if (sessionExpired) {
    throw new Error('session_expired');
  }

  if (!response.ok) {
    throw new ApiResponseError(response.status, 'Failed to fetch notices');
  }

  return response.json();
};

// お知らせを既読/未読に更新
export const readNotice = async (
  noticeId: number,
  isRead: boolean,
): Promise<void> => {
  const { response, sessionExpired } = await fetchWithSessionHandling(
    `${API_BASE_URL}/api/notice/${noticeId}/read`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ is_read: isRead }),
    },
  );

  if (handleRateLimitResponse(response)) {
    throw new ApiResponseError(429, 'rate_limited');
  }

  if (sessionExpired) {
    return;
  }

  if (!response.ok) {
    throw new ApiResponseError(response.status, 'Failed to update notice');
  }
};
